import { useMemo, useState } from 'react';
import { Patient } from '../../../utils/api-objects/Patient';
import { getPatientSubscriptionType } from '../utils/subscriptionTypes';

export interface PatientFilters {
  searchTerm: string;
  selectedNutritionist: string | null;
  selectedPhysician: string | null;
  selectedSubscriptionFilter: string; // Single subscription filter: 'all', 'subs', or specific type ID
}

export interface UsePatientFiltersReturn {
  filters: PatientFilters;
  filteredPatients: Patient[];
  setSearchTerm: (term: string) => void;
  setSelectedNutritionist: (name: string | null) => void;
  setSelectedPhysician: (name: string | null) => void;
  setSelectedSubscriptionFilter: (filter: string) => void;
  resetFilters: () => void;
  hasActiveFilters: boolean;
}

// Function to aggregate patients by user_id
const aggregatePatientsByUserId = (patients: Patient[]): Patient[] => {
  const patientMap = new Map<string, Patient>();

  patients.forEach((patient) => {
    const existingPatient = patientMap.get(patient.user_id);
    
    if (!existingPatient) {
      // First occurrence of this user_id
      patientMap.set(patient.user_id, patient);
    } else {
      // Merge with existing patient record
      const mergedPatient: Patient = {
        ...existingPatient,
        // Combine products arrays
        products: Array.from(new Set([...existingPatient.products, ...patient.products])),
        // Combine bloodcodes arrays
        bloodcodes: Array.from(new Set([...existingPatient.bloodcodes, ...patient.bloodcodes])),
        // Use the most recent created_at
        created_at: new Date(patient.created_at) > new Date(existingPatient.created_at) 
          ? patient.created_at 
          : existingPatient.created_at,
        // Use the most recent status (prioritize active over canceled)
        status: patient.status === 'active' || existingPatient.status === 'active' 
          ? 'active' 
          : patient.status,
        // Prioritize sub_type from active subscription, then from most recent
        sub_type: (() => {
          // If current patient is active, use its sub_type
          if (patient.status === 'active' && patient.sub_type) {
            return patient.sub_type;
          }
          // If existing patient is active, use its sub_type
          if (existingPatient.status === 'active' && existingPatient.sub_type) {
            return existingPatient.sub_type;
          }
          // Otherwise, use the most recent non-empty sub_type
          return patient.sub_type || existingPatient.sub_type;
        })(),
        // Use the most recent swabcode (prioritize non-null)
        swabcode: patient.swabcode || existingPatient.swabcode,
      };
      
      patientMap.set(patient.user_id, mergedPatient);
    }
  });

  return Array.from(patientMap.values());
};

export const usePatientFilters = (patients: Patient[] | null): UsePatientFiltersReturn => {
  const [filters, setFilters] = useState<PatientFilters>({
    searchTerm: '',
    selectedNutritionist: null,
    selectedPhysician: null,
    selectedSubscriptionFilter: 'all', // Default to show all users
  });

  const filteredPatients = useMemo(() => {
    if (!patients) return [];

    // First, aggregate patients by user_id to remove duplicates
    const aggregatedPatients = aggregatePatientsByUserId(patients);

    return aggregatedPatients.filter((patient) => {
      // Search filter
      if (filters.searchTerm) {
        const fullName = `${patient.user_name} ${patient.user_family_name}`.toLowerCase();
        const email = patient.email.toLowerCase();
        const swabcode = patient.swabcode || "";
        const lastBloodcode = patient.bloodcodes[patient.bloodcodes.length - 1] || "";
        
        const searchLower = filters.searchTerm.toLowerCase();
        const matchesSearch = 
          fullName.includes(searchLower) ||
          email.includes(searchLower) ||
          swabcode.includes(filters.searchTerm) ||
          lastBloodcode.includes(filters.searchTerm);
        
        if (!matchesSearch) return false;
      }

      // Subscription filter
      if (filters.selectedSubscriptionFilter !== 'all') {
        if (filters.selectedSubscriptionFilter === 'subs') {
          // Show only users with any subscription
          const hasSubscription = patient.sub_type && patient.sub_type !== "";
          if (!hasSubscription) return false;
        } else {
          // Show only users with specific subscription type
          const patientType = getPatientSubscriptionType(patient);
          if (!patientType || patientType.id !== filters.selectedSubscriptionFilter) {
            return false;
          }
        }
      }

      // Nutritionist filter - only apply if selected
      if (filters.selectedNutritionist && patient.nutritionist?.email !== filters.selectedNutritionist) {
        return false;
      }

      // Physician filter - only apply if selected
      if (filters.selectedPhysician && patient.physician?.email !== filters.selectedPhysician) {
        return false;
      }

      return true;
    });
  }, [patients, filters]);

  const setSearchTerm = (term: string) => {
    setFilters(prev => ({ ...prev, searchTerm: term }));
  };

  const setSelectedNutritionist = (email: string | null) => {
    setFilters(prev => ({ 
      ...prev, 
      selectedNutritionist: email
    }));
  };

  const setSelectedPhysician = (email: string | null) => {
    setFilters(prev => ({ 
      ...prev, 
      selectedPhysician: email
    }));
  };

  const setSelectedSubscriptionFilter = (filter: string) => {
    setFilters(prev => ({ 
      ...prev, 
      selectedSubscriptionFilter: filter
    }));
  };

  const resetFilters = () => {
    setFilters({
      searchTerm: '',
      selectedNutritionist: null,
      selectedPhysician: null,
      selectedSubscriptionFilter: 'all',
    });
  };

  const hasActiveFilters = filters.searchTerm !== '' || 
    filters.selectedNutritionist !== null || 
    filters.selectedPhysician !== null ||
    filters.selectedSubscriptionFilter !== 'all';

  return {
    filters,
    filteredPatients,
    setSearchTerm,
    setSelectedNutritionist,
    setSelectedPhysician,
    setSelectedSubscriptionFilter,
    resetFilters,
    hasActiveFilters,
  };
}; 